import { BedSingle, ShowerHead, Users } from "lucide-react";
import { motion } from "framer-motion";
import type { RoomCategory } from "../config/rooms";
import { siteConfig } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";
import { SmartImage } from "./SmartImage";
import { InquiryButton } from "./BookingButton";

export const RoomCard = ({ room, index = 0, detailed = false }: { room: RoomCategory; index?: number; detailed?: boolean }) => {
  const { t, tr } = useLanguage();
  return (
    <motion.article className="card group flex h-full flex-col overflow-hidden" whileHover={{ y: -4 }} transition={{ duration: 0.25 }} data-testid={`room-card-${room.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        <SmartImage src={room.image} alt={`${tr(room.name)} – ${t.common.singleBeds}`} isPlaceholder={room.imageIsPlaceholder} className="h-full w-full" imgClassName="transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        <span className="absolute right-3 top-3 rounded-full bg-cream/90 px-3 py-1 text-xs font-semibold text-charcoal backdrop-blur" data-testid={`room-card-${room.id}-count`}>{t.common.roomsAvailable(room.rooms)}</span>
        <span className="absolute -bottom-px left-0 font-display text-[5rem] leading-none text-cream/90 drop-shadow-sm pl-4" aria-hidden>{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-7">
        <h3 className="font-display text-xl font-medium tracking-tight" data-testid={`room-card-${room.id}-name`}>{tr(room.name)}</h3>
        <p className="mt-1.5 flex items-baseline gap-1.5 whitespace-nowrap" data-testid={`room-card-${room.id}-price`}>
          <span className="font-display text-lg font-medium text-clay">{t.common.from} {siteConfig.price.perBedPerNight} {siteConfig.price.currency}</span>
          <span className="text-xs text-charcoal-500">· {t.common.perBedNight}</span>
        </p>
        {detailed && <p className="mt-3 text-sm text-charcoal-500 leading-relaxed">{tr(room.description)}</p>}
        <ul className="mt-5 grid gap-2.5 text-sm text-charcoal-700">
          <li className="flex items-center gap-2.5"><BedSingle className="h-4 w-4 text-clay" aria-hidden /><span data-testid={`room-card-${room.id}-beds`}>{room.beds} {t.common.singleBeds}</span></li>
          <li className="flex items-center gap-2.5"><Users className="h-4 w-4 text-clay" aria-hidden /><span>{t.common.guests(room.beds)}</span></li>
          <li className="flex items-center gap-2.5"><ShowerHead className="h-4 w-4 text-clay" aria-hidden /><span data-testid={`room-card-${room.id}-bath`}>{t.common.sharedBath}</span></li>
        </ul>
        <div className="mt-6 pt-2 mt-auto">
          <InquiryButton variant="dark" className="w-full" label={t.cta.inquireRoom} testId={`room-card-${room.id}-inquire`} />
        </div>
      </div>
    </motion.article>
  );
};
